import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import AdminPageShell from '@/components/admin/AdminPageShell';
import ApartmentsDatabase from '@/components/admin/ApartmentsDatabase';
import BulkMatchListingsDialog from '@/components/admin/BulkMatchListingsDialog';
import EmailApprovalDialog from '@/components/emails/EmailApprovalDialog';
import { useLanguage } from '@/contexts/LanguageContext';

export default function AdminApartmentsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAptIds, setSelectedAptIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [draftQueue, setDraftQueue] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aptsRes, pmsRes] = await Promise.all([
        api.get('/apartments'),
        api.get('/property-managers'),
      ]);
      setApartments(aptsRes.data || []);
      setPropertyManagers(pmsRes.data || []);
    } catch {
      toast.error('Failed to load apartments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (aptId) => {
    if (!window.confirm('Delete this apartment?')) return;
    try {
      await api.delete(`/apartments/${aptId}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <AdminPageShell testId="admin-apartments-page">
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      testId="admin-apartments-page"
      breadcrumbs={[{ label: t('admin.apartments') }]}
    >
      <ApartmentsDatabase
        apartments={apartments}
        propertyManagers={propertyManagers}
        onDelete={handleDelete}
        selectedIds={selectedAptIds}
        onSelectionChange={setSelectedAptIds}
        onMatchListings={() => setBulkOpen(true)}
      />
      <BulkMatchListingsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        selectedApartments={apartments.filter((a) => selectedAptIds.includes(a.id))}
        onComplete={({ drafts }) => {
          setSelectedAptIds([]);
          load();
          const queue = (drafts || []).filter((d) => d?.id);
          if (queue.length) {
            setEmailDraft(queue[0]);
            setDraftQueue(queue.slice(1));
          }
        }}
      />
      <EmailApprovalDialog
        draft={emailDraft}
        open={!!emailDraft}
        onClose={() => {
          if (draftQueue.length) {
            setEmailDraft(draftQueue[0]);
            setDraftQueue((q) => q.slice(1));
          } else {
            setEmailDraft(null);
            load();
          }
        }}
      />
    </AdminPageShell>
  );
}
